import type { GenerationStatus, ReasonCode } from '@/api/generation';

interface TaskStatusAreaProps {
  status: GenerationStatus;
  reasonCode?: ReasonCode | null;
  onRetry: () => void;
  onModifyPrompt: () => void;
}

export default function TaskStatusArea({ status, reasonCode, onRetry, onModifyPrompt }: TaskStatusAreaProps) {
  const { t } = useTranslation();

  if (status === 'success') return null;

  const renderContent = () => {
    switch (status) {
      case 'created':
      case 'queued':
        return (
          <>
            {/* Animated spinner */}
            <div className="relative w-28 h-28 flex items-center justify-center">
              <div
                className="absolute inset-0 rounded-full opacity-20 animate-ping"
                style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)', animationDuration: '1.5s' }}
              />
              <div
                className="absolute inset-2 rounded-full opacity-30"
                style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
              />
              <div
                className="relative w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
              >
                <i className="ri-time-line text-white text-2xl animate-pulse"></i>
              </div>
              <div
                className="absolute inset-0 rounded-full border-2 border-dashed border-pink-300/60 animate-spin"
                style={{ animationDuration: '6s' }}
              />
            </div>
            <div className="text-center">
              <p className="text-base font-bold text-gray-800">{t('gen_status_queued')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('gen_aiCreatingDesc')}</p>
            </div>
          </>
        );

      case 'generating':
        return (
          <>
            <div className="relative w-28 h-28 flex items-center justify-center">
              <div
                className="absolute inset-0 rounded-full opacity-20 animate-ping"
                style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)', animationDuration: '1.5s' }}
              />
              <div
                className="absolute inset-2 rounded-full opacity-30"
                style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
              />
              <div
                className="relative w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
              >
                <i className="ri-sparkling-2-fill text-white text-2xl animate-pulse"></i>
              </div>
              <div
                className="absolute inset-0 rounded-full border-2 border-dashed border-pink-300/60 animate-spin"
                style={{ animationDuration: '6s' }}
              />
            </div>
            <div className="text-center">
              <p className="text-base font-bold text-gray-800">{t('gen_status_generating')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('gen_aiCreatingDesc')}</p>
            </div>
            <div className="w-52 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,107,157,0.15)' }}>
              <div className="h-full rounded-full animate-progress" style={{ background: 'linear-gradient(90deg, #FF6B9D, #A855F7)' }} />
            </div>
            <div className="px-4 py-2 rounded-full text-xs text-gray-500" style={{ background: 'rgba(255,255,255,0.7)' }}>
              ✨ {t('gen_highQuality')}
            </div>
          </>
        );

      case 'auditing':
        return (
          <>
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
            >
              <i className="ri-eye-line text-white text-2xl animate-pulse"></i>
            </div>
            <div className="text-center">
              <p className="text-base font-bold text-gray-800">{t('gen_status_auditing')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('gen_aiCreatingDesc')}</p>
            </div>
          </>
        );

      case 'failed':
        return (
          <>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-red-50">
              <i className="ri-error-warning-line text-red-400 text-2xl"></i>
            </div>
            <div className="text-center">
              <p className="text-base font-bold text-gray-800">{t('gen_status_failed')}</p>
              <p className="text-sm text-gray-500 mt-1">
                {reasonCode ? t(`gen_reason_${reasonCode}`) : t('gen_reason_default')}
              </p>
            </div>
            <button
              onClick={onRetry}
              className="px-6 py-2.5 rounded-2xl text-white text-sm font-bold cursor-pointer active:scale-95 transition-all"
              style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
            >
              {t('gen_tryAgain')}
            </button>
          </>
        );

      case 'blocked':
        return (
          <>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-orange-50">
              <i className="ri-shield-cross-line text-orange-400 text-2xl"></i>
            </div>
            <div className="text-center">
              <p className="text-base font-bold text-gray-800">{t('gen_status_blocked')}</p>
              <p className="text-sm text-gray-500 mt-1">
                {reasonCode ? t(`gen_reason_${reasonCode}`) : t('gen_reason_default')}
              </p>
            </div>
            <button
              onClick={onModifyPrompt}
              className="px-6 py-2.5 rounded-2xl text-white text-sm font-bold cursor-pointer active:scale-95 transition-all"
              style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
            >
              {t('gen_modifyPrompt')}
            </button>
          </>
        );

      case 'timeout':
        return (
          <>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-gray-50">
              <i className="ri-time-line text-gray-400 text-2xl"></i>
            </div>
            <div className="text-center">
              <p className="text-base font-bold text-gray-800">{t('gen_status_timeout')}</p>
            </div>
            <button
              onClick={onRetry}
              className="px-6 py-2.5 rounded-2xl text-white text-sm font-bold cursor-pointer active:scale-95 transition-all"
              style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
            >
              {t('gen_tryAgain')}
            </button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div
      data-testid="generation-task-status"
      data-generation-status={status}
      aria-label={`generation-status-${status}`}
      className="mt-8 flex flex-col items-center gap-5"
    >
      {renderContent()}
    </div>
  );
}
